import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, TextField, Button, Alert, Snackbar, IconButton, 
    InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

const ExternalTenantApiSettings: React.FC = () => {
    // Provisioning states
    const [tenantName, setTenantName] = useState('');
    const [rawApiKey, setRawApiKey] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // Ledger Fetch States
    const [tenants, setTenants] = useState<any[]>([]);
    
    // TopUp Modal States
    const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
    const [btcAmount, setBtcAmount] = useState<number | ''>('');
    const [remarks, setRemarks] = useState('');
    const [topUpLoading, setTopUpLoading] = useState(false);
    const [topUpError, setTopUpError] = useState('');

    const fetchTenants = async () => {
        try {
            const res = await axios.get('/api/admin/tenants',getAxiosConfig());
            setTenants(res.data);
        } catch (err) {
            console.error("Failed to fetch tenants list", err);
        }
    };

    useEffect(() => {
        fetchTenants();
    }, []);

    const handleProvision = async () => {
        if (!tenantName.trim()) {
            setError('Tenant name is required.');
            return;
        }

        setLoading(true);
        setError('');
        setRawApiKey('');

        try {
            const response = await axios.post(API_PATH+'/admin/tenants/provision',  { tenantName: tenantName.trim() }, getAxiosConfig());
            if (response.data && response.data.rawApiKey) {
                setRawApiKey(response.data.rawApiKey);
                setTenantName('');
                fetchTenants(); // Refresh Ledger table!
            }
        } catch (err: any) {
            setError(err.response?.data || 'Failed to provision tenant API key.');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(rawApiKey);
        setToastMessage('API Key copied to clipboard!');
    };

    const handleTopUpSubmit = async () => {
        if (!selectedTenant) return;
        if (!btcAmount || btcAmount <= 0) {
            setTopUpError('Valid BTC amount is required.');
            return;
        }

        setTopUpLoading(true);
        setTopUpError('');

        try {
            await axios.post(`/api/admin/tenants/${selectedTenant}/top-up`, { btcAmount, remarks },getAxiosConfig());
            setToastMessage(`Successfully added ${btcAmount} BTC to ${selectedTenant}`);
            setSelectedTenant(null); // Close modal
            setBtcAmount('');
            setRemarks('');
            fetchTenants(); // Refresh data!
        } catch (err: any) {
            setTopUpError(err.response?.data || 'Failed to top up balance.');
        } finally {
            setTopUpLoading(false);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2, minHeight: 'calc(100vh - 64px)' }}>
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    mb: 3,
                    backgroundColor: '#d1e8ff',
                    padding: { xs: 1, sm: 1.5 },
                    borderRadius: 1,
                }}
            >
                <Typography variant="h5" sx={{ fontFamily: 'monospace', color: '#1565c0', width: '100%' }}>
                    Tenant APIs & Ledgers
                </Typography>
            </Box>

            {/* List and TopUp Block */}
            <Paper sx={{ p: 4, mb: 4 }} elevation={3}>
                <Typography variant="h6" gutterBottom>
                    Tenant Ledger Ecosystem
                </Typography>
                
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Tenant Name</strong></TableCell>
                                <TableCell><strong>Ledger Balance (BTC)</strong></TableCell>
                                <TableCell align="center"><strong>Manage Balance</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tenants.map((t, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{t.tenantName}</TableCell>
                                    <TableCell>{t.balanceBtc.toFixed(6)}</TableCell>
                                    <TableCell align="center">
                                        <Button 
                                            variant="outlined" 
                                            size="small" 
                                            onClick={() => setSelectedTenant(t.tenantName)}
                                        >
                                            Top Up
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {tenants.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">No tenants provisioned yet.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Provisioning Block */}
            <Paper sx={{ p: 4, margin: '0 auto' }} elevation={3}>
                <Typography variant="h6" gutterBottom>
                    Generate Secure Tenant API Key
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Provisioning a new tenant will generate a secure API Key. The database only stores an irrecoverable SHA-256 hash. 
                    <strong> This key will only be shown ONCE.</strong>
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {!rawApiKey ? (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Tenant Code Name"
                            variant="outlined"
                            fullWidth
                            value={tenantName}
                            onChange={(e) => setTenantName(e.target.value)}
                            disabled={loading}
                        />
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleProvision}
                            disabled={loading || !tenantName.trim()}
                            size="large"
                        >
                            {loading ? 'Provisioning...' : 'Provision New Key'}
                        </Button>
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Alert severity="success">
                            Successfully provisioned API Key for new Tenant. Please copy it immediately!
                        </Alert>
                        <TextField
                            label="Raw API Key"
                            variant="outlined"
                            fullWidth
                            value={rawApiKey}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleCopy} color="primary">
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button 
                            variant="outlined" 
                            color="secondary" 
                            onClick={() => setRawApiKey('')}
                            sx={{ mt: 1 }}
                        >
                            Clear Screen (Done Copying)
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Top Up Modal */}
            <Dialog open={!!selectedTenant} onClose={() => setSelectedTenant(null)} fullWidth maxWidth="sm">
                <DialogTitle>Top Up Ledger: <strong>{selectedTenant}</strong></DialogTitle>
                <DialogContent dividers>
                    {topUpError && <Alert severity="error" sx={{ mb: 2 }}>{topUpError}</Alert>}
                    
                    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Deposit Amount (BTC)"
                            type="number"
                            fullWidth
                            value={btcAmount}
                            onChange={(e) => setBtcAmount(parseFloat(e.target.value))}
                            disabled={topUpLoading}
                        />
                        <TextField
                            label="Internal Remarks / Reference"
                            fullWidth
                            multiline
                            rows={3}
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            disabled={topUpLoading}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedTenant(null)} disabled={topUpLoading}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleTopUpSubmit} disabled={topUpLoading || !btcAmount}>
                        Confirm Deposit
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!toastMessage}
                autoHideDuration={3000}
                onClose={() => setToastMessage('')}
                message={toastMessage}
            />
        </Box>
    );
};

export default ExternalTenantApiSettings;
